import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Download, KeyRound, Mail, RefreshCw, HelpCircle } from "lucide-react";

// Public manager-facing Usage Report page.
// Route: /hotel/usage-report (linked from every organisation's manager email,
// the /organisations tiles, and Parents Corner on activated devices).
//
// Flow:
//   Step 1  Login: manager email + persistent organisation access code
//   Step 1a (from step 1)  Forgot code → we email a fresh one to the registered address
//   Step 2  Report view: warm Poodle Talk theme, overview + daily/weekly + breakdowns + PDF download

const API_BASE = "/api/hotel/usage-report";

async function postJson(path, body) {
  const r = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data?.detail || `HTTP ${r.status}`), { data });
  return data;
}

async function getJson(path) {
  const r = await fetch(path);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data?.detail || `HTTP ${r.status}`), { data });
  return data;
}

const fmt = (n) => (Number(n) || 0).toLocaleString();
const fmtDT = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d) ? "—" : d.toLocaleString(); };

export default function HotelUsageReport({ onBack }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [token, setToken] = useState("");
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [venueFilter, setVenueFilter] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");

  const doLogin = async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      const r = await postJson(`${API_BASE}/login`, {
        manager_email: email.trim(),
        access_code: code.trim(),
      });
      setToken(r.report_token);
      setStep(2);
    } catch (e) {
      setErr(e?.data?.detail || e.message || "Login failed.");
    } finally { setBusy(false); }
  };

  const doReset = async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      await postJson(`${API_BASE}/reset`, { manager_email: resetEmail.trim() || email.trim() });
      setInfo("If that email is on file, a fresh access code is on its way. Please check your inbox in a minute.");
      setShowForgot(false);
    } catch (e) {
      // Endpoint always returns 200 to prevent enumeration, but network errors can still occur.
      setErr(e?.data?.detail || e.message || "Could not request a reset.");
    } finally { setBusy(false); }
  };

  const loadReport = useCallback(async (t = token, d = days) => {
    if (!t) return;
    setBusy(true); setErr("");
    try {
      const r = await getJson(`${API_BASE}?token=${encodeURIComponent(t)}&days=${d}`);
      setReport(r);
    } catch (e) {
      setErr(e?.data?.detail || e.message || "Could not load report.");
    } finally { setBusy(false); }
  }, [token, days]);

  useEffect(() => { if (step === 2 && token) loadReport(token, days); /* eslint-disable-next-line */ }, [step, token, days]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col" data-testid="hotel-usage-report">
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E0E0E0] px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="p-2 rounded-full hover:bg-[#F3F0E6] transition-colors" data-testid="usage-report-back">
              <ArrowLeft className="w-6 h-6 text-[#4A4A4A]" />
            </button>
          )}
          <h1 className="text-lg sm:text-xl font-semibold text-[#4A4A4A]">Usage Report</h1>
          <div className="ml-auto text-xs text-[#9A9A9A] hidden sm:block">Poodle Talk · Organisation manager access</div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-6 sm:py-10">
        {err && (
          <div className="bg-[#FFF0F0] border border-[#F5D0D0] text-[#A55B64] rounded-xl p-3 text-sm mb-4" data-testid="usage-report-error">{err}</div>
        )}
        {info && (
          <div className="bg-[#EEF6EA] border border-[#CDE3C1] text-[#4A7A35] rounded-xl p-3 text-sm mb-4" data-testid="usage-report-info">{info}</div>
        )}

        {step === 1 && (
          <section className="max-w-lg mx-auto bg-white rounded-3xl border-2 border-[#E8D4A8] p-6 sm:p-8 shadow-sm" data-testid="usage-report-step-1">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-[#FFF7D6] text-[#B89823] rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide mb-3">
                <KeyRound className="w-3.5 h-3.5" /> Manager sign-in
              </div>
              <h2 className="text-2xl font-bold text-[#4A4A4A] mb-1">View your organisation's Usage Report</h2>
              <p className="text-sm text-[#6B6B6B]">One sign-in shows every venue, activation code and device — a single credit balance.</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wide">Manager email</label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A9A]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="manager@yourhotel.co.uk"
                    data-testid="usage-report-email-input"
                    autoComplete="email"
                    className="w-full pl-10 pr-3 py-2.5 bg-[#FDFBF7] border-2 border-[#E8E5DC] rounded-xl text-sm focus:outline-none focus:border-[#E8D4A8]"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wide">Usage report access code</label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX-XXXX"
                  data-testid="usage-report-code-input"
                  className="w-full mt-1 px-3 py-2.5 bg-[#FDFBF7] border-2 border-[#E8E5DC] rounded-xl text-lg font-mono tracking-[0.2em] focus:outline-none focus:border-[#E8D4A8]"
                />
                <p className="text-[11px] text-[#9A9A9A] mt-1">Different from your building activation codes — one code per organisation, sent when you first purchased.</p>
              </div>
              <button
                onClick={doLogin}
                disabled={busy || !email.trim() || !code.trim()}
                data-testid="usage-report-login"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#4A4A4A] text-white rounded-full font-semibold text-sm hover:bg-black transition-all disabled:opacity-50"
              >
                {busy ? "Signing in…" : "View my usage report"}
              </button>
              <button
                onClick={() => { setShowForgot((v) => !v); setResetEmail(email); setErr(""); setInfo(""); }}
                data-testid="usage-report-forgot-toggle"
                className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-[#6B6B6B] hover:text-[#4A4A4A]"
              >
                <HelpCircle className="w-3.5 h-3.5" /> Forgot your access code?
              </button>

              {showForgot && (
                <div className="mt-2 border-2 border-[#E8E5DC] bg-[#FDFBF7] rounded-xl p-4 space-y-2" data-testid="usage-report-forgot-panel">
                  <p className="text-xs text-[#6B6B6B]">We'll email a fresh access code to the manager address we have on file. Any previous code will stop working.</p>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="manager@yourhotel.co.uk"
                    data-testid="usage-report-reset-email"
                    className="w-full px-3 py-2 bg-white border-2 border-[#E8E5DC] rounded-lg text-sm focus:outline-none focus:border-[#E8D4A8]"
                  />
                  <button
                    onClick={doReset}
                    disabled={busy || !(resetEmail.trim() || email.trim())}
                    data-testid="usage-report-reset-submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold text-sm hover:bg-[#D9C599] transition-all disabled:opacity-50"
                  >
                    {busy ? "Sending…" : "Email me a fresh code"}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {step === 2 && (
          <ReportView
            report={report}
            busy={busy}
            days={days}
            setDays={setDays}
            onReload={() => loadReport(token, days)}
            token={token}
            venueFilter={venueFilter}
            setVenueFilter={setVenueFilter}
            deviceFilter={deviceFilter}
            setDeviceFilter={setDeviceFilter}
            onSignOut={() => { setStep(1); setToken(""); setReport(null); setCode(""); }}
          />
        )}
      </main>
    </div>
  );
}

function ReportView({ report, busy, days, setDays, onReload, token, venueFilter, setVenueFilter, deviceFilter, setDeviceFilter, onSignOut }) {
  const daily = report?.daily || [];
  const weekly = report?.weekly || [];
  const byCode = report?.by_code || [];
  const byDevice = report?.by_device || [];
  const individual = report?.individual || [];

  const maxDaily = useMemo(() => Math.max(1, ...daily.map((d) => d.credits_used || 0)), [daily]);
  const maxWeekly = useMemo(() => Math.max(1, ...weekly.map((d) => d.credits_used || 0)), [weekly]);

  const filteredIndividual = useMemo(() => {
    const v = venueFilter.trim().toLowerCase();
    const d = deviceFilter.trim().toLowerCase();
    return individual.filter((r) => {
      const venueMatch = !v || (r.venue_label || "").toLowerCase().includes(v) || (r.code || "").toLowerCase().includes(v);
      const devMatch = !d || (r.device_hint || "").toLowerCase().includes(d);
      return venueMatch && devMatch;
    });
  }, [individual, venueFilter, deviceFilter]);

  const pdfHref = token ? `/api/hotel/usage-report.pdf?token=${encodeURIComponent(token)}&days=${days}` : null;

  return (
    <div className="space-y-6" data-testid="usage-report-step-2">
      {/* Header row */}
      <div className="bg-white rounded-3xl border-2 border-[#E8D4A8] p-5 sm:p-6 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-semibold text-[#B89823] uppercase tracking-wide mb-1">
              {report?.sector ? `${report.sector.toString().charAt(0).toUpperCase()}${report.sector.slice(1)}` : "Organisation"} · Usage report
            </p>
            <h2 className="text-2xl font-bold text-[#4A4A4A]" data-testid="usage-report-hotel-name">{report?.hotel_name || "Your organisation"}</h2>
            <p className="text-xs text-[#6B6B6B] mt-1">
              Reporting period: <strong className="text-[#4A4A4A]">{fmtDT(report?.period_start)} → {fmtDT(report?.period_end)}</strong> · Generated {fmtDT(report?.generated_at)}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-[#6B6B6B]">Range</label>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              data-testid="usage-report-days"
              className="text-xs bg-white border-2 border-[#E8E5DC] rounded-full px-3 py-1.5 focus:outline-none focus:border-[#E8D4A8]"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
            </select>
            <button
              onClick={onReload}
              disabled={busy}
              data-testid="usage-report-refresh"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border-2 border-[#E8E5DC] hover:bg-[#F3F0E6] disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />{busy ? "…" : "Refresh"}
            </button>
            {pdfHref && (
              <a
                href={pdfHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="usage-report-pdf"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#E8D4A8] text-[#5A4A32] font-semibold hover:bg-[#D9C599]"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF report
              </a>
            )}
            <button onClick={onSignOut} data-testid="usage-report-signout" className="text-xs text-[#A55B64] hover:text-[#82454C] px-2">Sign out</button>
          </div>
        </div>
      </div>

      {/* Overview */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Opening credits" value={fmt(report?.opening_credits)} testid="stat-opening" />
        <Stat label="Membership added" value={fmt(report?.membership_credits_added)} testid="stat-membership" />
        <Stat label="Add-on added" value={fmt(report?.addon_credits_added)} testid="stat-addon" />
        <Stat label="Messages used" value={fmt(report?.credits_used)} testid="stat-used" />
        <Stat label="Refunds" value={fmt(report?.refunds)} testid="stat-refunds" />
        <Stat label="Credits remaining" value={fmt(report?.current_balance)} highlight testid="stat-remaining" />
      </section>

      {/* Daily */}
      <section className="bg-white rounded-3xl border-2 border-[#E8E5DC] p-5">
        <h3 className="text-sm font-bold text-[#4A4A4A] uppercase tracking-wide mb-3">Daily usage</h3>
        <div className="space-y-2" data-testid="usage-report-daily">
          {daily.length === 0 && <p className="text-xs text-[#9A9A9A]">No usage in this window yet.</p>}
          {daily.map((d) => (
            <div key={d.period} className="text-[12px]">
              <div className="flex justify-between text-[#4A4A4A] mb-1">
                <span className="font-medium">{d.period}</span>
                <span className="text-[#6B6B6B] font-mono">−{fmt(d.credits_used)} used · +{fmt(d.credits_added)} added{d.refunds ? ` · ${fmt(d.refunds)} refunded` : ""}</span>
              </div>
              <div className="w-full h-2 bg-[#F3F0E6] rounded">
                <div className="h-2 bg-[#E8D4A8] rounded" style={{ width: `${(d.credits_used / maxDaily) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Weekly */}
      <section className="bg-white rounded-3xl border-2 border-[#E8E5DC] p-5">
        <h3 className="text-sm font-bold text-[#4A4A4A] uppercase tracking-wide mb-3">Weekly usage</h3>
        <div className="space-y-2" data-testid="usage-report-weekly">
          {weekly.length === 0 && <p className="text-xs text-[#9A9A9A]">No usage in this window yet.</p>}
          {weekly.map((d) => (
            <div key={d.period} className="text-[12px]">
              <div className="flex justify-between text-[#4A4A4A] mb-1">
                <span className="font-medium">Week {d.period}</span>
                <span className="text-[#6B6B6B] font-mono">−{fmt(d.credits_used)} used · +{fmt(d.credits_added)} added{d.refunds ? ` · ${fmt(d.refunds)} refunded` : ""}</span>
              </div>
              <div className="w-full h-2 bg-[#F3F0E6] rounded">
                <div className="h-2 bg-[#B89823] rounded" style={{ width: `${(d.credits_used / maxWeekly) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Breakdowns */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BreakdownCard title="By building / venue and code" rows={byCode} testid="usage-report-by-code" />
        <BreakdownCard title="By device" rows={byDevice} testid="usage-report-by-device" />
      </section>

      {/* Individual deductions */}
      <section className="bg-white rounded-3xl border-2 border-[#E8E5DC] p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="text-sm font-bold text-[#4A4A4A] uppercase tracking-wide">Individual credit deductions</h3>
          <div className="flex items-center gap-2">
            <input
              value={venueFilter}
              onChange={(e) => setVenueFilter(e.target.value)}
              placeholder="Filter venue / code"
              data-testid="usage-report-filter-venue"
              className="text-xs px-3 py-1.5 bg-[#FDFBF7] border-2 border-[#E8E5DC] rounded-full focus:outline-none focus:border-[#E8D4A8]"
            />
            <input
              value={deviceFilter}
              onChange={(e) => setDeviceFilter(e.target.value)}
              placeholder="Filter device"
              data-testid="usage-report-filter-device"
              className="text-xs px-3 py-1.5 bg-[#FDFBF7] border-2 border-[#E8E5DC] rounded-full focus:outline-none focus:border-[#E8D4A8]"
            />
          </div>
        </div>
        <p className="text-[11px] text-[#9A9A9A] mb-2">Time · venue · device · credits. Child chat text is never included here.</p>
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-[12px]" data-testid="usage-report-individual">
            <thead className="bg-[#FFF7D6]">
              <tr className="text-left text-[#6B6B6B]">
                <th className="px-2 py-2">When</th>
                <th className="px-2 py-2">Venue · Code</th>
                <th className="px-2 py-2">Device</th>
                <th className="px-2 py-2 text-right">Credits</th>
                <th className="px-2 py-2 text-right">Balance after</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F0E6]">
              {filteredIndividual.length === 0 && (
                <tr><td colSpan={5} className="px-2 py-6 text-center text-[#9A9A9A]">No deductions match this filter.</td></tr>
              )}
              {filteredIndividual.slice(0, 500).map((r, i) => (
                <tr key={i} className="hover:bg-[#FDFBF7]">
                  <td className="px-2 py-1.5 text-[#4A4A4A] whitespace-nowrap">{fmtDT(r.at)}</td>
                  <td className="px-2 py-1.5 text-[#4A4A4A]">{r.venue_label || "—"} <span className="text-[#9A9A9A] font-mono">· {r.code || ""}</span></td>
                  <td className="px-2 py-1.5 text-[#4A4A4A]">{r.device_hint || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[#A55B64]">−{fmt(r.amount)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[#6B6B6B]">{r.balance_after != null ? fmt(r.balance_after) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredIndividual.length > 500 && (
          <p className="text-[11px] text-[#9A9A9A] mt-2 text-center">Showing the first 500 rows. Download the PDF for the full period breakdown.</p>
        )}
      </section>

      <p className="text-[11px] text-[#9A9A9A] text-center pt-2">
        This is a read-only report. To change codes, devices or memberships, contact your account admin.
      </p>
    </div>
  );
}

function Stat({ label, value, highlight, testid }) {
  return (
    <div className={`rounded-2xl p-3 border-2 ${highlight ? "bg-[#FFF7D6] border-[#E8D4A8]" : "bg-white border-[#E8E5DC]"}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-[#6B6B6B] mb-1">{label}</p>
      <p className={`text-lg font-bold ${highlight ? "text-[#B89823]" : "text-[#4A4A4A]"}`}>{value}</p>
    </div>
  );
}

function BreakdownCard({ title, rows, testid }) {
  return (
    <div className="bg-white rounded-3xl border-2 border-[#E8E5DC] p-5" data-testid={testid}>
      <h3 className="text-sm font-bold text-[#4A4A4A] uppercase tracking-wide mb-3">{title}</h3>
      {(!rows || rows.length === 0) && <p className="text-xs text-[#9A9A9A]">No usage in this window.</p>}
      <ul className="space-y-1 max-h-72 overflow-y-auto">
        {(rows || []).map((r, i) => (
          <li key={i} className="flex items-center justify-between text-[12px] text-[#4A4A4A] py-1 border-b border-[#F3F0E6] last:border-0">
            <span className="truncate mr-3">{r.label}</span>
            <span className="font-mono">{fmt(r.credits_used)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
